import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {copyStyles} from './copyStyles';
import {closePopOutWindow, openPopOutWindow} from './popOutWindowHost';

const CLOSE_POLL_INTERVAL_MS = 1000;

export const PopOutWindow: React.FC<{
    children: React.ReactNode;
    remotePopOut?: Window | null;
    title?: string;
    onUnload?: () => void;
}> = ({children, remotePopOut, title, onUnload}) => {
    const [container] = useState(() => document.createElement('div'));
    const popOutRef = useRef<Window | null>(null);

    // make sure we have a ref to a popOut
    useEffect(() => {
        if (remotePopOut) {
            popOutRef.current = remotePopOut;
        }
        if (!popOutRef.current) {
            popOutRef.current = openPopOutWindow();
        }
        if (popOutRef.current) {
            popOutRef.current.document.title = title ?? 'Pexip One';
        }
    }, [remotePopOut, title]);

    useEffect(() => {
        const popOutCopy = popOutRef.current;
        if (!popOutCopy) {
            return;
        }

        let pollingId: number | undefined;
        let isReleased = false;

        const releasePopOut = () => {
            if (isReleased) {
                return;
            }
            isReleased = true;
            if (pollingId !== undefined) {
                window.clearInterval(pollingId);
                pollingId = undefined;
            }
            popOutCopy.removeEventListener('beforeunload', releasePopOut);
            window.removeEventListener('beforeunload', releasePopOut);
            if (!popOutCopy.closed) {
                closePopOutWindow(popOutCopy);
            }
            if (typeof onUnload === 'function') {
                onUnload();
            }
        };

        popOutCopy.document.body.appendChild(container);
        copyStyles(document, popOutCopy);
        popOutCopy.addEventListener('beforeunload', releasePopOut);
        window.addEventListener('beforeunload', releasePopOut);
        // Native hosts close the window without delivering `beforeunload`.
        pollingId = window.setInterval(() => {
            if (popOutCopy.closed) {
                releasePopOut();
            }
        }, CLOSE_POLL_INTERVAL_MS);

        return () => releasePopOut();
    }, [container, onUnload]);

    if (!container) {
        return null;
    }
    return createPortal(children, container);
};
